/* eslint-disable max-classes-per-file */

import coalesceGlobalErrorMessage from '@app/libs/coalesceGlobalErrorMessage';
import getExtensionFromFileName from '@app/libs/file/getExtensionFromFileName';
import formatFileSize from '@app/libs/formatter/formatFileSize';

enum ErrorType {
  FILE_NUMBER_LIMIT_EXCEEDED = 'FILE_NUMBER_LIMIT_EXCEEDED',
  FILE_SIZE_LIMIT_EXCEEDED = 'FILE_SIZE_LIMIT_EXCEEDED',
  FILE_NAME_LENGTH_EXCEEDED = 'FILE_NAME_LENGTH_EXCEEDED',
  FILE_NAME_VALIDATION = 'FILE_NAME_VALIDATION',
  INVALID_EXTENSION = 'INVALID_EXTENSION',
  NO_VALID_CONTRACT_FILE = 'NO_VALID_CONTRACT_FILE',
  OTHER = 'OTHER',
}

type Error =
  | {
      type: ErrorType.FILE_NUMBER_LIMIT_EXCEEDED;
      maxFileLimit: number;
    }
  | {
      type: ErrorType.FILE_SIZE_LIMIT_EXCEEDED;
      maxFileSize: number;
    }
  | {
      type: ErrorType.FILE_NAME_LENGTH_EXCEEDED;
      maxFileNameLength: number;
    }
  | {
      type: ErrorType.FILE_NAME_VALIDATION;
      invalidCharacters: string;
    }
  | {
      type: ErrorType.INVALID_EXTENSION;
      extension: string;
    }
  | {
      type: ErrorType.NO_VALID_CONTRACT_FILE;
    }
  | {
      type: ErrorType.OTHER;
      message: string;
    };

export interface IFileUploadError {
  readonly fileName: string;
  readonly documentName?: string;
  getErrorMessages(): string[];
}

export class FileUploadErrorBuilder {
  private fileName: string;

  private errors: Error[];

  private documentName?: string;

  constructor(fileName: string) {
    this.fileName = fileName;
    this.errors = [];
  }

  setDocumentName(name: string) {
    this.documentName = name;
    return this;
  }

  fileNumberExceededError(maxFileLimit: number) {
    this.errors.push({ type: ErrorType.FILE_NUMBER_LIMIT_EXCEEDED, maxFileLimit });
    return this;
  }

  fileExtensionError() {
    this.errors.push({
      type: ErrorType.INVALID_EXTENSION,
      extension: getExtensionFromFileName(this.fileName),
    });
    return this;
  }

  fileNameLengthExceededError(maxFileNameLength: number) {
    this.errors.push({ type: ErrorType.FILE_NAME_LENGTH_EXCEEDED, maxFileNameLength });
    return this;
  }

  fileNameValidationError(invalidCharacters: string) {
    this.errors.push({ type: ErrorType.FILE_NAME_VALIDATION, invalidCharacters });
    return this;
  }

  fileSizeExceededError(maxFileSize: number) {
    this.errors.push({ type: ErrorType.FILE_SIZE_LIMIT_EXCEEDED, maxFileSize });
    return this;
  }

  noValidContractFileError() {
    this.errors.push({ type: ErrorType.NO_VALID_CONTRACT_FILE });
    return this;
  }

  otherError(message: string) {
    this.errors.push({ type: ErrorType.OTHER, message });
    return this;
  }

  build(): IFileUploadError | null {
    if (this.errors.length === 0) {
      return null;
    }

    return new FileUploadErrorBuilder.FileUploadError({
      errors: this.errors,
      fileName: this.fileName,
      documentName: this.documentName,
    });
  }

  get currentErrorsCount() {
    return this.errors.length;
  }

  private static FileUploadError = class implements IFileUploadError {
    readonly fileName: string;

    readonly documentName?: string;

    private errors: Error[];

    constructor(params: { fileName: string; errors: Error[]; documentName?: string }) {
      this.fileName = params.fileName;
      this.documentName = params.documentName;
      this.errors = params.errors;
    }

    private static getErrorMessage(error: Error): string {
      switch (error.type) {
        case ErrorType.FILE_NUMBER_LIMIT_EXCEEDED:
          return `업로드 최대 개수 ${error.maxFileLimit}개 초과`;
        case ErrorType.FILE_SIZE_LIMIT_EXCEEDED:
          return `파일 용량 ${formatFileSize(error.maxFileSize)} 초과`;
        case ErrorType.INVALID_EXTENSION:
          return `지원하지 않는 파일 형식`;
        case ErrorType.NO_VALID_CONTRACT_FILE:
          return '계약서로 설정할 수 있는 파일 없음';
        case ErrorType.FILE_NAME_LENGTH_EXCEEDED:
          return `최대 파일명 길이 ${error.maxFileNameLength.toLocaleString()}자 초과`;
        case ErrorType.FILE_NAME_VALIDATION:
          return `파일명에 ${error.invalidCharacters} 포함 업로드 불가`;
        case ErrorType.OTHER:
        default:
          return coalesceGlobalErrorMessage(error.message);
      }
    }

    public getErrorMessages(): string[] {
      return this.errors.map(FileUploadErrorBuilder.FileUploadError.getErrorMessage);
    }
  };
}
